import React, { useState } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { FormValidation, RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';

import { Alert, Button } from '@mui/material';

import { queueGiftCardDailySummary } from '~/services/ReportingService';
import { addMonths, formatDateISO, startOfMonth } from '~/services/TimeService';

import ErrorDisplay from '../ErrorDisplay';

interface FormData {
  startAt?: string;
  endAt?: string;
}

function validateForm(formData: FormData, errors: FormValidation): FormValidation {
  if (!formData.startAt) {
    errors.startAt.addError('Please enter a start date');
  }
  if (!formData.endAt) {
    errors.endAt.addError('Please enter an end date');
  }
  return errors;
}

const formSchema: RJSFSchema = {
  description:
    'Please enter a date range to export the gift card daily summary report. Defaults to the full prior month.',
  type: 'object',
  properties: {
    startAt: {
      title: 'Select Start Date',
      type: 'string',
      format: 'date',
      default: formatDateISO(addMonths(-1, startOfMonth())),
    },
    endAt: {
      title: 'Select End Date',
      type: 'string',
      format: 'date',
      default: formatDateISO(addMonths(0, startOfMonth())),
    },
  },
};

const uiSchema = {
  startAt: {
    'ui:widget': 'date',
  },
  endAt: {
    'ui:widget': 'date',
  },
};

export default function ExportGiftCardDailySummaryForm() {
  const [successMessage, setSuccessMessage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (event: IChangeEvent<FormData>) => {
    setError(null);
    setSuccessMessage(false);

    queueGiftCardDailySummary({
      startAt: event.formData.startAt,
      endAt: event.formData.endAt,
    })
      .then(() => {
        setSuccessMessage(true);
      })
      .catch((err) => {
        setError(err.message);
      });
  };

  return (
    <Form
      schema={formSchema}
      uiSchema={uiSchema}
      onSubmit={handleSubmit}
      customValidate={validateForm}
      validator={validator}
      showErrorList={false}
    >
      <Button type="submit" variant="contained">
        Email Gift Card Daily Summary Report
      </Button>

      {error && <ErrorDisplay message={error} />}

      {successMessage && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Your report is queued for delivery and will be emailed shortly.
        </Alert>
      )}
    </Form>
  );
}
